import { apiUrl } from "../runtime.js";

/**
 * Browser ("open in browser") sign-in: standard Discord web OAuth for when the
 * app runs in a plain tab instead of a Discord Activity. Ends in the SAME bearer
 * token the Activity flow produces, so the backend gate is unchanged.
 * All storage access is try/catch — private windows may refuse it.
 */

export interface StoredToken {
  accessToken: string;
  /** Epoch ms when Discord says the token expires. */
  expiresAt: number;
}

export type CallbackResult =
  | { kind: "none" }
  | { kind: "code"; code: string }
  | { kind: "error"; error: string };

const EXPIRY_MARGIN_MS = 5 * 60_000;
const SCOPES = "identify guilds.members.read";

/** "/api/deltatape" → "deltatape" (storage-key namespace per app). */
export function appSlug(appPath: string): string {
  return appPath.replace(/^\/api\//, "").replace(/\/.*$/, "");
}
const tokenKey = (appPath: string): string => `2skies.${appSlug(appPath)}.browserAuth`;
const stateKey = (appPath: string): string => `2skies.${appSlug(appPath)}.oauthState`;
const silentKey = (appPath: string): string => `2skies.${appSlug(appPath)}.silentTried`;

function safeGet(store: Storage, key: string): string | null {
  try { return store.getItem(key); } catch { return null; }
}
function safeSet(store: Storage, key: string, value: string): void {
  try { store.setItem(key, value); } catch { /* storage refused — degrade to per-page */ }
}
function safeRemove(store: Storage, key: string): void {
  try { store.removeItem(key); } catch { /* ignore */ }
}

export function loadToken(appPath: string): StoredToken | null {
  const raw = safeGet(localStorage, tokenKey(appPath));
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<StoredToken>;
    return typeof v.accessToken === "string" && typeof v.expiresAt === "number"
      ? { accessToken: v.accessToken, expiresAt: v.expiresAt }
      : null;
  } catch {
    return null;
  }
}

export function saveToken(appPath: string, token: StoredToken): void {
  safeSet(localStorage, tokenKey(appPath), JSON.stringify(token));
}

/** Sign-out / rejected token: forget the token and reset the silent guard. */
export function clearBrowserAuth(appPath: string): void {
  safeRemove(localStorage, tokenKey(appPath));
  clearSilentAttempt(appPath);
}

export function isTokenExpired(token: StoredToken, now: number = Date.now()): boolean {
  return now >= token.expiresAt - EXPIRY_MARGIN_MS;
}

/** One silent (prompt=none) re-authorise per browser session — prevents redirect loops. */
export function canAttemptSilent(appPath: string): boolean {
  return safeGet(sessionStorage, silentKey(appPath)) === null;
}
export function markSilentAttempt(appPath: string): void {
  safeSet(sessionStorage, silentKey(appPath), "1");
}
export function clearSilentAttempt(appPath: string): void {
  safeRemove(sessionStorage, silentKey(appPath));
}

export function buildAuthorizeUrl(p: { clientId: string; browserUrl: string; state: string; silent: boolean }): string {
  const u = new URL("https://discord.com/oauth2/authorize");
  u.searchParams.set("client_id", p.clientId);
  u.searchParams.set("response_type", "code");
  u.searchParams.set("scope", SCOPES);
  u.searchParams.set("redirect_uri", p.browserUrl);
  u.searchParams.set("state", p.state);
  if (p.silent) u.searchParams.set("prompt", "none");
  return u.toString();
}

/** Redirect to Discord's login. `navigate` is injectable for tests. */
export function beginSignIn(
  p: { appPath: string; clientId: string; browserUrl: string; silent: boolean },
  navigate: (url: string) => void = (url) => window.location.assign(url),
): void {
  const state = crypto.randomUUID();
  safeSet(sessionStorage, stateKey(p.appPath), state);
  if (p.silent) markSilentAttempt(p.appPath);
  navigate(buildAuthorizeUrl({ clientId: p.clientId, browserUrl: p.browserUrl, state, silent: p.silent }));
}

/** Read (and strip) Discord's ?code / ?error callback params, verifying state. */
export function completeSignInFromUrl(appPath: string): CallbackResult {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const error = params.get("error");
  if (!code && !error) return { kind: "none" };

  window.history.replaceState(null, "", window.location.pathname + window.location.hash);
  const expected = safeGet(sessionStorage, stateKey(appPath));
  safeRemove(sessionStorage, stateKey(appPath));

  if (error) return { kind: "error", error };
  if (!code || !expected || params.get("state") !== expected) return { kind: "error", error: "state_mismatch" };
  return { kind: "code", code };
}

/** Swap an OAuth code for a token via our backend (which holds the client secret). */
export async function exchangeBrowserCode(
  appPath: string,
  code: string,
  browserUrl: string,
  now: number = Date.now(),
): Promise<StoredToken> {
  const res = await fetch(apiUrl(`${appPath}/auth/exchange`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, redirect_uri: browserUrl }),
  });
  if (res.status === 503) throw new Error("Discord is busy right now — please try again in a moment.");
  if (!res.ok) throw new Error(`Sign-in failed (${res.status})`);
  const body = (await res.json()) as { access_token: string; expires_in?: number };
  const ttlMs = (body.expires_in ?? 604_800) * 1000;
  return { accessToken: body.access_token, expiresAt: now + ttlMs };
}
